import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, Play, Check, SkipForward, Clock, Loader2, X, Printer, RotateCcw, Ban,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatDateTime, formatMoney } from '@/lib/api'

interface OPEtapa {
  id: number
  etapa_id: number
  etapa_codigo: string
  etapa_nome: string
  etapa_cor: string
  etapa_icone: string
  ordem: number
  status: string
  iniciada_em: string | null
  concluida_em: string | null
  operador_id: number | null
  maquina_id: number | null
  quantidade_ok: string
  quantidade_refugo: string
  observacao: string | null
}

interface OP {
  id: number
  numero: number
  produto_id: number
  produto_descricao: string | null
  ficha_id: number | null
  variante: string
  maquina_id: number | null
  molde_id: number | null
  deposito_id: number
  pedido_id: number | null
  pedido_numero: number | null
  quantidade: string
  quantidade_produzida: string
  quantidade_refugo: string
  custo_previsto: string
  custo_real: string
  custo_mp?: string
  custo_mod?: string
  custo_maquina?: string
  custo_overhead?: string
  status: string
  inicio_previsto: string | null
  fim_previsto: string | null
  iniciada_em: string | null
  concluida_em: string | null
  observacao: string | null
  criado_em: string
  etapas: OPEtapa[]
}

interface Apontamento {
  id: number
  etapa_op_id: number | null
  usuario_id: number | null
  operador_nome: string | null
  maquina_id: number | null
  maquina_codigo: string | null
  turno_id: number | null
  turno_nome: string | null
  motivo_parada_id: number | null
  motivo_parada_nome: string | null
  motivo_parada_categoria: string | null
  motivo_refugo_id: number | null
  motivo_refugo_nome: string | null
  motivo_refugo_reciclavel: boolean | null
  tipo: string
  quantidade: string
  peso_kg: string
  duracao_seg: number
  parametros: Record<string, any>
  observacao: string | null
  criado_em: string
}

const TIPO_META: Record<string, { label: string; cor: string; icone: string }> = {
  inicio:        { label: 'Início',          cor: 'bg-blue-100 text-blue-800',       icone: '▶' },
  parcial:       { label: 'Apontamento',     cor: 'bg-violet-100 text-violet-800',   icone: '+' },
  pausa:         { label: 'Pausa',           cor: 'bg-amber-100 text-amber-800',     icone: '⏸' },
  retorno:       { label: 'Retomar',         cor: 'bg-emerald-100 text-emerald-800', icone: '▶' },
  fim:           { label: 'Fim',             cor: 'bg-emerald-100 text-emerald-800', icone: '✓' },
  refugo:        { label: 'Refugo',          cor: 'bg-red-100 text-red-800',         icone: '⚠' },
  setup_inicio:  { label: 'Setup início',    cor: 'bg-blue-100 text-blue-800',       icone: '🛠' },
  setup_fim:     { label: 'Setup fim',       cor: 'bg-blue-100 text-blue-800',       icone: '🛠' },
  obs:           { label: 'Observação',      cor: 'bg-ink-100 text-ink-700',         icone: '✎' },
  sos:           { label: 'SOS',             cor: 'bg-rose-100 text-rose-800',       icone: '🚨' },
}

function fmtDuracao(seg: number): string {
  if (seg < 60) return `${seg}s`
  if (seg < 3600) return `${Math.floor(seg / 60)}min`
  return `${Math.floor(seg / 3600)}h${Math.floor((seg % 3600) / 60)}min`
}

const ETAPA_COR: Record<string, string> = {
  pendente: 'bg-ink-100 border-ink-300 text-ink-600',
  em_execucao: 'bg-amber-50 border-amber-400 text-amber-800',
  concluida: 'bg-emerald-50 border-emerald-400 text-emerald-800',
  pulada: 'bg-ink-50 border-ink-200 text-ink-400 line-through',
}

export default function OrdemProducaoDetalhe() {
  const { id } = useParams<{ id: string }>()
  const qc = useQueryClient()
  const [concluirEtapa, setConcluirEtapa] = useState<OPEtapa | null>(null)

  const { data: op, isLoading } = useQuery<OP>({
    queryKey: ['op', id],
    queryFn: async () => (await api.get(`/industrial/ordens/${id}`)).data,
    enabled: !!id,
  })

  const { data: apontamentos = [] } = useQuery<Apontamento[]>({
    queryKey: ['op-apontamentos', id],
    queryFn: async () => (await api.get(`/industrial/ordens/${id}/apontamentos`)).data,
    enabled: !!id,
  })

  const iniciarEtapa = useMutation({
    mutationFn: async (etapaId: number) =>
      (await api.post(`/industrial/ordens/${id}/etapas/${etapaId}/iniciar`)).data,
    onSuccess: () => {
      toast.success('Etapa iniciada')
      qc.invalidateQueries({ queryKey: ['op', id] })
      qc.invalidateQueries({ queryKey: ['op-apontamentos', id] })
    },
  })

  const pularEtapa = useMutation({
    mutationFn: async (etapaId: number) =>
      (await api.post(`/industrial/ordens/${id}/etapas/${etapaId}/pular`, null, { params: { motivo: 'Pulada via UI' } })).data,
    onSuccess: () => {
      toast.success('Etapa pulada')
      qc.invalidateQueries({ queryKey: ['op', id] })
    },
  })

  if (isLoading || !op) return <div className="p-6">Carregando…</div>

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/industrial/ordens" className="text-ink-500 hover:text-ink-900"><ArrowLeft size={18} /></Link>
        <div>
          <h1 className="text-2xl font-bold">OP #{op.numero}</h1>
          <p className="text-ink-600 text-sm">
            {op.produto_descricao || `Produto #${op.produto_id}`} · variante <strong>{op.variante}</strong>
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => window.open(`/industrial/ordens/imprimir?ids=${op.id}`, '_blank')}
            className="px-3 py-1.5 bg-ink-100 hover:bg-ink-200 rounded text-sm flex items-center gap-1"
            title="Imprimir cartão da OP com QR"
          >
            <Printer size={14} /> Imprimir
          </button>
          {(op.status === 'em_producao' || op.status === 'planejada') && (
            <button
              onClick={async () => {
                const motivo = prompt('Motivo pra reiniciar a OP?')
                if (motivo === null) return
                try {
                  await api.post(`/industrial/ordens/${op.id}/reiniciar`, { motivo })
                  toast.success('OP reiniciada')
                  qc.invalidateQueries({ queryKey: ['op', id] })
                } catch (e: any) {
                  toast.error(e?.response?.data?.detail || 'Erro')
                }
              }}
              className="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded text-sm flex items-center gap-1"
            >
              <RotateCcw size={14} /> Reiniciar
            </button>
          )}
          <span className={`text-sm px-3 py-1 rounded ${
            op.status === 'planejada' ? 'bg-ink-200 text-ink-700' :
            op.status === 'em_producao' ? 'bg-amber-100 text-amber-800' :
            op.status === 'concluida' ? 'bg-emerald-100 text-emerald-800' :
            'bg-rose-100 text-rose-700'
          }`}>{op.status.replace('_', ' ')}</span>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="Qtd planejada" value={Number(op.quantidade).toLocaleString('pt-BR')} />
        <KPI label="Qtd produzida" value={Number(op.quantidade_produzida).toLocaleString('pt-BR')} accent="emerald" />
        <KPI label="Refugo" value={Number(op.quantidade_refugo).toLocaleString('pt-BR')} accent="rose" />
        <KPI label="Custo previsto" value={formatMoney(op.custo_previsto)} />
        <KPI label="Custo real" value={formatMoney(op.custo_real)} accent="amber" />
      </div>

      {/* Decomposição do custo */}
      {Number(op.custo_real) > 0 && (
        <div className="card card-body">
          <h3 className="text-sm font-semibold mb-2">Composição do custo</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <CustoItem label="Matéria-prima" valor={Number(op.custo_mp || 0)} cor="bg-emerald-500" />
            <CustoItem label="Mão de obra" valor={Number(op.custo_mod || 0)} cor="bg-sky-500" />
            <CustoItem label="Hora-máquina" valor={Number(op.custo_maquina || 0)} cor="bg-amber-500" />
            <CustoItem label="Overhead" valor={Number(op.custo_overhead || 0)} cor="bg-violet-500" />
          </div>
        </div>
      )}

      {/* Metadata */}
      <div className="card card-body grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <Info label="Pedido">{op.pedido_numero ? `#${op.pedido_numero}` : '-'}</Info>
        <Info label="Depósito destino">#{op.deposito_id}</Info>
        <Info label="Máquina">{op.maquina_id ? `#${op.maquina_id}` : '-'}</Info>
        <Info label="Molde">{op.molde_id ? `#${op.molde_id}` : '-'}</Info>
        <Info label="Iniciada em">{formatDateTime(op.iniciada_em)}</Info>
        <Info label="Concluída em">{formatDateTime(op.concluida_em)}</Info>
        <Info label="Previsto início">{formatDateTime(op.inicio_previsto)}</Info>
        <Info label="Previsto fim">{formatDateTime(op.fim_previsto)}</Info>
      </div>

      {op.observacao && (
        <div className="card card-body text-sm">
          <div className="font-medium text-ink-700 mb-1">Observação</div>
          <div className="text-ink-600">{op.observacao}</div>
        </div>
      )}

      {/* Timeline etapas */}
      <div>
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Clock size={18} className="text-brand-600" /> Fluxo de etapas
        </h2>
        {op.etapas.length === 0 && (
          <div className="card card-body text-center text-ink-500">
            Esta OP não tem etapas de roteiro configuradas.
          </div>
        )}
        <div className="space-y-2">
          {op.etapas.map(ep => (
            <div key={ep.id} className={`border-l-4 rounded p-3 ${ETAPA_COR[ep.status] || 'bg-ink-50'}`}
              style={ep.status === 'pendente' ? { borderLeftColor: ep.etapa_cor } : undefined}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center font-mono text-xs font-bold"
                  style={{ backgroundColor: ep.etapa_cor, color: 'white' }}>{ep.ordem}</div>
                <div className="flex-1">
                  <div className="font-medium">{ep.etapa_nome}</div>
                  <div className="text-xs opacity-75">
                    <span className="font-mono">{ep.etapa_codigo}</span> · status <strong>{ep.status}</strong>
                    {ep.iniciada_em && <> · iniciada {formatDateTime(ep.iniciada_em)}</>}
                    {ep.concluida_em && <> · concluída {formatDateTime(ep.concluida_em)}</>}
                  </div>
                  {(Number(ep.quantidade_ok) > 0 || Number(ep.quantidade_refugo) > 0) && (
                    <div className="text-xs mt-1">
                      OK: <strong>{ep.quantidade_ok}</strong>
                      {Number(ep.quantidade_refugo) > 0 && <> · Refugo: <strong className="text-rose-600">{ep.quantidade_refugo}</strong></>}
                    </div>
                  )}
                  {ep.observacao && <div className="text-xs mt-1 italic opacity-75">{ep.observacao}</div>}
                </div>
                <div className="flex gap-2">
                  {op.status === 'em_producao' && ep.status === 'pendente' && (
                    <>
                      <button onClick={() => iniciarEtapa.mutate(ep.id)} disabled={iniciarEtapa.isPending}
                        className="btn-secondary !py-1 !px-2 text-xs">
                        <Play size={12} /> Iniciar
                      </button>
                      <button onClick={() => pularEtapa.mutate(ep.id)} disabled={pularEtapa.isPending}
                        className="btn-secondary !py-1 !px-2 text-xs">
                        <SkipForward size={12} /> Pular
                      </button>
                    </>
                  )}
                  {op.status === 'em_producao' && ep.status === 'em_execucao' && (
                    <>
                      <button onClick={() => setConcluirEtapa(ep)}
                        className="btn-primary !py-1 !px-2 text-xs">
                        <Check size={12} /> Concluir
                      </button>
                      <button
                        onClick={async () => {
                          const motivo = prompt('Motivo pra cancelar?')
                          if (motivo === null) return
                          try {
                            await api.post(`/industrial/ordens/${op.id}/etapas/${ep.id}/cancelar`, null, { params: { motivo } })
                            toast.success('Etapa cancelada')
                            qc.invalidateQueries({ queryKey: ['op', id] })
                            qc.invalidateQueries({ queryKey: ['op-apontamentos', id] })
                          } catch (e: any) {
                            toast.error(e?.response?.data?.detail || 'Erro')
                          }
                        }}
                        className="!py-1 !px-2 text-xs bg-rose-100 hover:bg-rose-200 text-rose-700 rounded inline-flex items-center gap-1"
                        title="Cancelar etapa (volta pra pendente)"
                      >
                        <Ban size={12} /> Cancelar
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Saídas (produtos acabados) — só aparece se houver entrada gerada */}
      <SaidasSection opId={op.id} status={op.status} />

      {/* Lote de produção (rastreabilidade) */}
      <LoteSection opId={op.id} />


      {/* Apontamentos — histórico enriquecido */}
      {apontamentos.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-3">Histórico de apontamentos ({apontamentos.length})</h2>
          <div className="card card-body p-0 overflow-hidden">
            <div className="divide-y divide-ink-100">
              {apontamentos.map(a => {
                const meta = TIPO_META[a.tipo] || { label: a.tipo, cor: 'bg-ink-100 text-ink-700', icone: '•' }
                const motivo = a.motivo_parada_nome || a.motivo_refugo_nome
                const qtd = Number(a.quantidade)
                const peso = Number(a.peso_kg)
                const params = a.parametros || {}
                const paramKeys = Object.keys(params)
                return (
                  <div key={a.id} className="px-3 py-2 hover:bg-ink-50">
                    <div className="flex items-start gap-2 flex-wrap">
                      <span className="text-ink-500 text-xs w-32 shrink-0 font-mono pt-0.5">
                        {formatDateTime(a.criado_em)}
                      </span>
                      <span className={`text-[11px] px-2 py-0.5 rounded font-semibold flex items-center gap-1 ${meta.cor}`}>
                        <span>{meta.icone}</span> {meta.label}
                      </span>
                      <div className="flex-1 min-w-0">
                        {motivo && (
                          <span className={`inline-block text-xs px-1.5 py-0.5 rounded mr-2 ${
                            a.motivo_refugo_nome
                              ? (a.motivo_refugo_reciclavel ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700')
                              : 'bg-amber-50 text-amber-700'
                          }`}>
                            {motivo}{a.motivo_refugo_reciclavel ? ' ♻' : ''}
                          </span>
                        )}
                        {a.observacao && (
                          <span className="text-sm text-ink-700">{a.observacao}</span>
                        )}
                      </div>
                      {qtd > 0 && (
                        <span className="font-mono text-sm font-semibold whitespace-nowrap">
                          {qtd.toLocaleString('pt-BR')} un
                        </span>
                      )}
                      {peso > 0 && (
                        <span className="font-mono text-sm font-semibold whitespace-nowrap text-ink-600">
                          {peso.toLocaleString('pt-BR')} kg
                        </span>
                      )}
                      {a.duracao_seg > 0 && (
                        <span className="font-mono text-xs text-ink-500 whitespace-nowrap">
                          ⏱ {fmtDuracao(a.duracao_seg)}
                        </span>
                      )}
                    </div>
                    {/* Linha 2: contexto (operador, máquina, turno, parâmetros) */}
                    {(a.operador_nome || a.maquina_codigo || a.turno_nome || paramKeys.length > 0) && (
                      <div className="ml-32 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-500">
                        {a.operador_nome && <span>👤 {a.operador_nome}</span>}
                        {a.maquina_codigo && <span>🏭 {a.maquina_codigo}</span>}
                        {a.turno_nome && <span>⏰ {a.turno_nome}</span>}
                        {paramKeys.map(k => (
                          <span key={k} className="bg-blue-50 text-blue-800 px-1.5 py-0.5 rounded font-mono">
                            {k}={String(params[k])}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {concluirEtapa && (
        <ConcluirEtapaModal
          opId={op.id} etapa={concluirEtapa}
          qtdPlanejada={Number(op.quantidade)}
          onClose={() => setConcluirEtapa(null)}
          onDone={() => {
            setConcluirEtapa(null)
            qc.invalidateQueries({ queryKey: ['op', id] })
            qc.invalidateQueries({ queryKey: ['op-apontamentos', id] })
          }}
        />
      )}
    </div>
  )
}

function ConcluirEtapaModal({ opId, etapa, qtdPlanejada, onClose, onDone }: {
  opId: number; etapa: OPEtapa; qtdPlanejada: number; onClose: () => void; onDone: () => void
}) {
  const [qtdOk, setQtdOk] = useState(String(qtdPlanejada))
  const [qtdRef, setQtdRef] = useState('0')
  const [obs, setObs] = useState('')

  const concluir = useMutation({
    mutationFn: async () => (await api.post(`/industrial/ordens/${opId}/etapas/${etapa.id}/concluir`, {
      quantidade_ok: parseFloat(qtdOk),
      quantidade_refugo: parseFloat(qtdRef || '0'),
      observacao: obs || null,
    })).data,
    onSuccess: () => { toast.success(`Etapa ${etapa.etapa_nome} concluída`); onDone() },
  })

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="font-semibold">Concluir {etapa.etapa_nome}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <label className="block">
            <div className="text-xs font-medium text-ink-700 mb-1">Quantidade OK *</div>
            <input type="number" step="0.001" className="input w-full" value={qtdOk} onChange={e => setQtdOk(e.target.value)} />
          </label>
          <label className="block">
            <div className="text-xs font-medium text-ink-700 mb-1">Refugo nesta etapa</div>
            <input type="number" step="0.001" className="input w-full" value={qtdRef} onChange={e => setQtdRef(e.target.value)} />
          </label>
          <label className="block">
            <div className="text-xs font-medium text-ink-700 mb-1">Observação</div>
            <textarea className="input w-full" rows={2} value={obs} onChange={e => setObs(e.target.value)} />
          </label>
        </div>
        <div className="border-t px-5 py-3 flex justify-end gap-2 bg-ink-50">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button disabled={concluir.isPending} onClick={() => concluir.mutate()} className="btn-primary">
            {concluir.isPending && <Loader2 size={14} className="animate-spin" />}
            Confirmar
          </button>
        </div>
      </div>
    </div>
  )
}

function LoteSection({ opId }: { opId: number }) {
  const { data: lotes } = useQuery<any[]>({
    queryKey: ['lotes-da-op', opId],
    queryFn: async () => (await api.get('/industrial/lotes')).data,
  })
  const lote = lotes?.find((l: any) => l.op_id === opId)
  if (!lote) return null
  return (
    <div>
      <h2 className="text-lg font-semibold mb-3">Lote de produção</h2>
      <div className="card card-body">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs text-ink-500 uppercase">Código</div>
            <Link to={`/industrial/lotes`} className="font-mono font-semibold text-brand-700 hover:underline">
              {lote.codigo}
            </Link>
          </div>
          <div>
            <div className="text-xs text-ink-500 uppercase">Status</div>
            <div className="font-semibold capitalize">{lote.status}</div>
          </div>
          <div>
            <div className="text-xs text-ink-500 uppercase">Produção</div>
            <div className="font-semibold">{lote.data_producao || '-'}</div>
          </div>
          <div>
            <div className="text-xs text-ink-500 uppercase">Validade</div>
            <div className="font-semibold">
              {lote.data_validade || '— sem controle —'}
              {lote.dias_para_vencer !== null && lote.dias_para_vencer !== undefined && (
                <span className={`ml-2 text-xs ${lote.dias_para_vencer <= 30 ? 'text-amber-700' : 'text-ink-500'}`}>
                  ({lote.dias_para_vencer}d)
                </span>
              )}
            </div>
          </div>
          <div>
            <div className="text-xs text-ink-500 uppercase">Qtd produzida</div>
            <div className="font-semibold">{lote.quantidade_produzida}</div>
          </div>
        </div>
      </div>
    </div>
  )
}

function CustoItem({ label, valor, cor }: { label: string; valor: number; cor: string }) {
  const fmt = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
  return (
    <div className="flex items-center gap-2">
      <div className={`w-3 h-10 rounded-sm ${cor} shrink-0`} />
      <div>
        <div className="text-xs text-ink-600">{label}</div>
        <div className="font-semibold">{fmt(valor)}</div>
      </div>
    </div>
  )
}

function KPI({ label, value, accent }: { label: string; value: string; accent?: 'emerald' | 'rose' | 'amber' }) {
  const colors: Record<string, string> = {
    emerald: 'text-emerald-700',
    rose: 'text-rose-700',
    amber: 'text-amber-700',
  }
  return (
    <div className="card card-body">
      <div className="text-xs text-ink-500 uppercase">{label}</div>
      <div className={`text-lg font-semibold ${accent ? colors[accent] : ''}`}>{value}</div>
    </div>
  )
}

function Info({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-ink-500 uppercase">{label}</div>
      <div className="font-medium">{children}</div>
    </div>
  )
}

interface SaidasPayload {
  total_saidas: number
  quantidade_total: number
  custo_real_total: number
  saidas: Array<{
    movimento_id: number
    produto_id: number
    produto_codigo: string
    produto_descricao: string
    quantidade: number
    deposito_id: number
    custo_rateado: number
    custo_unitario: number
    criado_em: string
  }>
}

function SaidasSection({ opId, status }: { opId: number; status: string }) {
  const { data } = useQuery<SaidasPayload>({
    queryKey: ['op-saidas', opId],
    queryFn: async () => (await api.get(`/industrial/ordens/${opId}/saidas`)).data,
    enabled: status === 'concluida',
  })
  if (!data || data.total_saidas === 0) return null
  const formatMoney = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
  return (
    <div>
      <h2 className="text-lg font-semibold mb-3">
        Produtos acabados ({data.total_saidas} {data.total_saidas === 1 ? 'saída' : 'saídas'})
      </h2>
      <div className="card card-body">
        <table className="table text-sm">
          <thead>
            <tr>
              <th>Produto</th>
              <th className="text-right">Qtd</th>
              <th className="text-right">Custo rateado</th>
              <th className="text-right">Custo unit.</th>
              <th>Depósito</th>
            </tr>
          </thead>
          <tbody>
            {data.saidas.map(s => (
              <tr key={s.movimento_id}>
                <td>
                  <div className="font-mono text-xs">{s.produto_codigo}</div>
                  <div>{s.produto_descricao}</div>
                </td>
                <td className="text-right font-semibold">{s.quantidade.toLocaleString('pt-BR')}</td>
                <td className="text-right">{s.custo_rateado > 0 ? formatMoney(s.custo_rateado) : '—'}</td>
                <td className="text-right">{s.custo_unitario > 0 ? formatMoney(s.custo_unitario) : '—'}</td>
                <td className="text-xs text-ink-600">#{s.deposito_id}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="font-semibold">
              <td className="text-right">Total:</td>
              <td className="text-right">{data.quantidade_total.toLocaleString('pt-BR')}</td>
              <td className="text-right">{formatMoney(data.custo_real_total)}</td>
              <td colSpan={2}></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}
